/**
 * THE ONE PLACE an opportunities enum becomes plain words (POLISH v1 rule 2: "plain words everywhere —
 * 'Voting open', not OPEN · no tiny capitals", and rule 1: one map from enum to words per module).
 *
 * Before this file the module printed raw enum tokens straight into badges on every page — `CRITICAL`,
 * `CLOSED_WON`, `RECEIVED`, `CALL_NOTES`, `FOLLOW_UP`, `PENDING` — in 10–12px capitals. A reader had to
 * already know the vocabulary. Every one of those now comes through here.
 *
 * `tone` travels WITH the words so colour and language never disagree (rule 3: meaning carried twice).
 * An unmapped value is shown as-is rather than hidden, so a new backend enum is visible, not silent.
 */
export type Tone = "accent" | "success" | "warning" | "danger" | "neutral" | "purple";

interface Word { label: string; tone: Tone }

const say = (label: string, tone: Tone = "neutral"): Word => ({ label, tone });

/** Pipeline stage. Stage NAMES come from the tenant's own `opportunityStages` rows — these are the
 *  fallbacks for the built-in codes, used where only the code is in hand (a progress note's old value). */
const STAGE: Record<string, Word> = {
  LEAD: say("New lead", "accent"),
  QUALIFYING: say("Qualifying", "accent"),
  QUALIFIED: say("Qualified", "accent"),
  PROPOSAL: say("Proposal sent", "warning"),
  NEGOTIATION: say("Negotiating", "warning"),
  DELIVERY: say("In delivery", "purple"),
  DELIVERED: say("Delivered", "success"),
  CLOSED_WON: say("Won", "success"),
  CLOSED_LOST: say("Lost", "danger"),
  ON_HOLD: say("On hold", "neutral"),
};

/** How urgent the deal is. Words, not shouted capitals. */
const PRIORITY: Record<string, Word> = {
  CRITICAL: say("Drop everything", "danger"),
  HIGH: say("High priority", "warning"),
  MEDIUM: say("Normal priority", "accent"),
  LOW: say("Low priority", "neutral"),
};

/** Whether the opportunity is still being worked. */
const STATUS: Record<string, Word> = {
  ACTIVE: say("Open", "accent"),
  WON: say("Won", "success"),
  LOST: say("Lost", "danger"),
  ON_HOLD: say("On hold", "neutral"),
};

/** A scheduled payment's own state (`PaymentScheduleStatus`). */
const PAYMENT: Record<string, Word> = {
  PENDING: say("Not due yet", "neutral"),
  PAID: say("Paid", "success"),
  OVERDUE: say("Overdue", "danger"),
  CANCELLED: say("Cancelled", "neutral"),
};

/** Has the money from the client landed? (`OpportunityPaymentStatus`) */
const MONEY_IN: Record<string, Word> = {
  PENDING: say("Waiting on the client", "neutral"),
  RECEIVED: say("Money received", "success"),
  DISTRIBUTED: say("Shared out to the team", "accent"),
  CANCELLED: say("Cancelled", "neutral"),
};

/** Has a team member's share gone out? */
const PAYOUT: Record<string, Word> = {
  PENDING: say("Not sent yet", "neutral"),
  SCHEDULED: say("Scheduled", "warning"),
  PROCESSING: say("Being paid", "warning"),
  PAID: say("Paid", "success"),
  FAILED: say("Payment failed", "danger"),
  CANCELLED: say("Cancelled", "neutral"),
};

/** What kind of job a task is. */
const TASK_TYPE: Record<string, Word> = {
  PROPOSAL: say("Proposal", "purple"),
  EMAIL: say("Email", "accent"),
  CALL: say("Phone call", "success"),
  MEETING: say("Meeting", "warning"),
  FOLLOW_UP: say("Follow-up", "warning"),
  DOCUMENT: say("Document", "neutral"),
  DEMO: say("Demo", "purple"),
  RESEARCH: say("Research", "neutral"),
  INTERNAL: say("Internal", "neutral"),
  OTHER: say("Other", "neutral"),
};

/** What kind of note it is. */
const NOTE_TYPE: Record<string, Word> = {
  GENERAL: say("General note", "neutral"),
  CALL_NOTES: say("Call notes", "accent"),
  MEETING_NOTES: say("Meeting notes", "purple"),
  EMAIL_SUMMARY: say("Email summary", "accent"),
  INTERNAL: say("Internal", "neutral"),
  CUSTOMER_FEEDBACK: say("Client feedback", "success"),
  COMPETITOR_INTEL: say("Competitor", "danger"),
  TECHNICAL_REQUIREMENTS: say("Technical needs", "neutral"),
  PRICING_DISCUSSION: say("Pricing talk", "warning"),
  DECISION_CRITERIA: say("How they will decide", "purple"),
  RISK_ASSESSMENT: say("Risk", "danger"),
  WIN_LOSS_ANALYSIS: say("Why we won or lost", "warning"),
  OTHER: say("Other", "neutral"),
};

/** A meeting's kind, shape and state. */
const MEETING_TYPE: Record<string, Word> = {
  INITIAL_CALL: say("First call", "accent"),
  DISCOVERY: say("Discovery", "accent"),
  DEMO: say("Demo", "purple"),
  PROPOSAL_PRESENTATION: say("Proposal walk-through", "warning"),
  NEGOTIATION: say("Negotiation", "warning"),
  CLOSING: say("Closing", "success"),
  CHECK_IN: say("Check-in", "accent"),
  REVIEW: say("Review", "purple"),
  OTHER: say("Meeting", "neutral"),
};

const MEETING_FORMAT: Record<string, Word> = {
  VIDEO_CALL: say("Video call", "accent"),
  PHONE_CALL: say("Phone call", "accent"),
  IN_PERSON: say("In person", "purple"),
  HYBRID: say("In person and online", "purple"),
};

const MEETING_STATUS: Record<string, Word> = {
  SCHEDULED: say("Coming up", "accent"),
  IN_PROGRESS: say("Happening now", "warning"),
  COMPLETED: say("Done", "success"),
  CANCELLED: say("Cancelled", "neutral"),
  NO_SHOW: say("Nobody came", "danger"),
  RESCHEDULED: say("Moved", "warning"),
};

/** How money arrives (`ValueStream.type`). */
const VALUE_STREAM: Record<string, Word> = {
  CASH: say("Cash", "success"),
  CRYPTO: say("Crypto", "warning"),
  EQUITY: say("Equity", "purple"),
  BARTER: say("Barter", "neutral"),
  OTHER: say("Other", "neutral"),
};

/** How we came to hear about them. Shown as words, so a blank reads as a question not a gap. */
const SENTIMENT: Record<string, Word> = {
  POSITIVE: say("Going well", "success"),
  NEUTRAL: say("Neither way", "neutral"),
  NEGATIVE: say("Going badly", "danger"),
};

/** Where a roadmap item has got to (module What's New). */
const ROADMAP_STATUS: Record<string, Word> = {
  completed: say("Shipped", "success"),
  "in-progress": say("Being built", "warning"),
  planned: say("Planned", "neutral"),
};

const VOCAB = {
  stage: STAGE,
  priority: PRIORITY,
  status: STATUS,
  payment: PAYMENT,
  moneyIn: MONEY_IN,
  payout: PAYOUT,
  taskType: TASK_TYPE,
  noteType: NOTE_TYPE,
  meetingType: MEETING_TYPE,
  meetingFormat: MEETING_FORMAT,
  meetingStatus: MEETING_STATUS,
  valueStream: VALUE_STREAM,
  sentiment: SENTIMENT,
  roadmapStatus: ROADMAP_STATUS,
};

export type Vocab = keyof typeof VOCAB;

/** Tidy fallback for a token this module has never seen: `CALL_NOTES` → `Call notes`, never left shouting. */
const humanise = (raw: string) =>
  raw.replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

/** The words for `value` in `vocab`. An unknown token is humanised, never hidden. */
export const words = (vocab: Vocab, value?: string | null): Word => {
  if (!value) return say("Not set", "neutral");
  return VOCAB[vocab][value] ?? say(humanise(value), "neutral");
};

/** Whether `value` is a token `vocab` actually knows — so free text is never re-cased as if it were one. */
export const knows = (vocab: Vocab, value?: string | null): boolean =>
  !!value && Object.prototype.hasOwnProperty.call(VOCAB[vocab], value);

/** Just the label — for a sentence, a title or an aria-label. */
export const label = (vocab: Vocab, value?: string | null): string => words(vocab, value).label;
